/**
 * AssessmentSectionOutline — the sticky section rail beside the
 * full-width draft (2026-07 redesign, post-generation stage).
 *
 * One entry per report section, each carrying its open-gap count from
 * the gap interview; clicking scrolls to the section's anchor. Hidden
 * below lg (the draft is the page on small screens).
 */

import { type ReactNode } from 'react';
import { Lightbulb } from 'lucide-react';
import {
  SECTION_DISPLAY_TITLES,
  sectionAnchorId,
  type AssessmentSectionKey,
} from '@/lib/assessmentSections';
import { Button } from '@/components/primitives/Button';

const OUTLINE_KEYS: readonly AssessmentSectionKey[] = [
  'history',
  'medical_assessment',
  'neurological',
  'sensory',
  'sleep',
  'pain',
  'cardiovascular',
  'respiratory',
  'gastroenterological',
  'urinary',
  'musculoskeletal',
  'integumentary',
  'endocrine',
  'functional_assessment',
  'social_assessment',
  'home_safety_assessment',
  'summary_and_recommendations',
];

export interface AssessmentSectionOutlineProps {
  gapCounts: Partial<Record<AssessmentSectionKey | 'header', number>>;
  /**
   * Founder polish (2026-07-06, item 4a): renders an "Add a
   * recommendation" entry under Summary and Recommendations that opens
   * the SAME insert picker the inline affordance does (the page owns the
   * picker's open state and scrolls to it). Absent = no entry.
   */
  onAddRecommendation?: () => void;
}

export function AssessmentSectionOutline({
  gapCounts,
  onAddRecommendation,
}: AssessmentSectionOutlineProps): ReactNode {
  const jump = (key: AssessmentSectionKey): void => {
    const el = document.getElementById(sectionAnchorId(key));
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      className="hidden lg:block sticky top-6 self-start"
      aria-label="Report sections"
      data-testid="assessment-section-outline"
    >
      <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-sage-400">
        Sections
      </p>
      <ul className="mt-2 space-y-0.5">
        {OUTLINE_KEYS.map((key) => {
          const count = gapCounts[key] ?? 0;
          return (
            <li key={key}>
              <Button
                variant="ghost"
                size="sm"
                className="w-full !justify-between !px-2 text-left font-medium"
                onClick={() => jump(key)}
                data-testid={`assessment-outline-${key}`}
              >
                <span className="truncate text-[12.5px]">{SECTION_DISPLAY_TITLES[key]}</span>
                {count > 0 && (
                  <span
                    className="ml-2 shrink-0 rounded-pill px-1.5 py-px text-[11px] font-semibold"
                    style={{ backgroundColor: '#FBF4E8', color: '#7a5a1f' }}
                    aria-label={`${count} open ${count === 1 ? 'gap' : 'gaps'}`}
                    data-testid="assessment-outline-gap-count"
                  >
                    {count}
                  </span>
                )}
              </Button>
              {/* FOUNDER RULING 2026-07-06: the add-recommendation entry
                  rides directly under Summary and Recommendations, in the
                  gold accent the founder ruled for this affordance
                  (gold-as-interactive exception; see
                  AssessmentRecommendationInsert). Opens the SAME picker. */}
              {key === 'summary_and_recommendations' && onAddRecommendation !== undefined && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Lightbulb size={13} aria-hidden="true" />}
                  className="w-full !justify-start !px-2 !pl-5 text-left font-bold !text-warm-700 hover:!bg-warm-50"
                  onClick={onAddRecommendation}
                  data-testid="assessment-outline-add-recommendation"
                >
                  <span className="truncate text-[12.5px]">Add a recommendation</span>
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
